import React, { createContext, useCallback, useContext, useState, Dispatch, SetStateAction } from 'react'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { ContentType } from '../../../../server/models/GenericContentFields'
import { type EmailContentType } from '../../../../server/models/EmailContent'
import { type InboxNewsArticleContentType } from '../../../../server/models/InboxNewsArticleContent'
import {
  isEmail,
  isArticle,
  type SourceType
} from '../../../requests/sources'
import { useEmailContentQuery } from '../../../requests/emailContent'
import { useNewsArticleContentQuery } from '../../../requests/newsArticleContent'
import {
  createCustomizedSourceContentWithBrandDetails
} from '../../../requests/customizedSourceContent'
import { handleError } from '../../../errors/handleError'

type SourceContentType = EmailContentType | InboxNewsArticleContentType

export type SourceContentContextType = {
  content: SourceContentType | null
  source: SourceType
  setContent: Dispatch<SetStateAction<SourceContentType | null>>
  setEnabled: (enabled: boolean) => void
  onSourceClick: () => void
  selectedContentTypes: ContentType[]

  generatingCustomizedContent: boolean
  createCustomizedContentWithBrandDetails: (params: {
    brandDetailsId: string
    contentId: string
    contentType: ContentType
  }) => Promise<void>
}

const SourceContentContext = createContext<SourceContentContextType | undefined>(undefined)

type Props = {
  children: React.ReactNode
  source: SourceType
  inboxId: string
  onSourceClick: (source: SourceType) => void
  selectedContentTypes: ContentType[]
}

export const SourceContentProvider: React.FC<Props> = ({
  inboxId,
  source,
  children,
  onSourceClick: inOnSourceClick,
  selectedContentTypes
}) => {
  const queryClient = useQueryClient()
  const [generatingCustomizedContent, setGeneratingCustomizedContent] = useState(false)
  const [enabled, setEnabled] = useState(false)

  const { data: emailContent } = useEmailContentQuery(
    inboxId,
    isEmail(source) ? source._id : '',
    {
      enabled: enabled && isEmail(source)
    }
  )

  const { data: articleContent } = useNewsArticleContentQuery(
    inboxId,
    isArticle(source) ? source._id : '',
    {
      enabled: enabled && isArticle(source)
    }
  )

  const setContent = useCallback((newContent: SourceContentType | null | ((prev: SourceContentType | null) => SourceContentType | null)) => {
    const currentContent = queryClient.getQueryData<SourceContentType>(['SourceContent', inboxId, source._id]) || null

    const contentToSet = typeof newContent === 'function' 
      ? newContent(currentContent)
      : newContent
    
    // Update the React Query cache
    queryClient.setQueryData(['sourceContent', inboxId, source._id], contentToSet)
  }, [queryClient, inboxId, source._id])

  const onSourceClick = useCallback(() => {
    inOnSourceClick(source)
  }, [source, inOnSourceClick])

  const createCustomizedContentWithBrandDetails = useCallback(async (params: {
    brandDetailsId: string
    contentId: string
    contentType: ContentType
  }) => {
    try {
      setGeneratingCustomizedContent(true)

      const res = await createCustomizedSourceContentWithBrandDetails({
        ...params,
        sourceId: source._id
      })

      const currentContent = queryClient.getQueryData<SourceContentType>(['sourceContent', inboxId, source._id])
      if (!currentContent) {
        throw new Error('Content not found. Please fetch content first.')
      }
      const newContent: SourceContentType = { ...currentContent }

      if (params.contentType === 'summary' && 'newThreeSentenceSummary' in res.data) {
        newContent.threeSentenceSummary = res.data.newThreeSentenceSummary
      }

      if (params.contentType === 'blog' && 'newBlog' in res.data) {
        newContent.blogs = newContent.blogs.map((blog) => {
          if (blog._id === params.contentId) {
            return {
              _id: blog._id,
              ...res.data.newBlog
            }
          }
          return blog
        })
      }

      if (params.contentType === 'carousel' && 'newCarousel' in res.data) {
        newContent.carousels = newContent.carousels.map((carousel) => {
          if (carousel._id === params.contentId) {
            return {
              _id: carousel._id,
              ...res.data.newCarousel
            }
          }
          return carousel
        })
      }

      if (params.contentType === 'shortVideo' && 'newShortVideoScript' in res.data) {
        newContent.shortVideoScript = newContent.shortVideoScript.map((shortVideo) => {
          if (shortVideo._id === params.contentId) {
            return {
              _id: shortVideo._id,
              ...res.data.newShortVideoScript
            }
          }
          return shortVideo
        })
      }

      // Update the React Query cache with the new content
      queryClient.setQueryData(['sourceContent', inboxId, source._id], newContent)

      toast.success('🔥 New content generated!')
    } catch (err) {
      handleError(err, 'Failed to create customized content with brand details. Please try again later.')
    }

    setGeneratingCustomizedContent(false)
  }, [source, inboxId, queryClient])

  return (
    <SourceContentContext.Provider value={{
      content: emailContent || articleContent || null,
      setContent,
      setEnabled,
      source,
      onSourceClick,
      selectedContentTypes,
      createCustomizedContentWithBrandDetails,
      generatingCustomizedContent
    }}>
      {children}
    </SourceContentContext.Provider>
  )
}

export const useSourceContent = (): SourceContentContextType => {
  const context = useContext(SourceContentContext)

  if (!context) {
    throw new Error('useSourceContent must be used within an SourceContentProvider')
  }

  return context
}
